<template>
    <DCard
        title="Preview New Pricing"
        padding
    >
        <DRow class="justify-content-center py-2">
            <h5 class="text-center">
                {{ __('contracts.pricing.messages.warning') }}
            </h5>
        </DRow>
        <DFormRow
            :label="__('contracts.pricing.preview_table.affected_customers')"
            is-required
        >
            {{ customersCount }}
        </DFormRow>
        <DFormRow
            :label="__('contracts.pricing.preview_table.affected_contracts')"
            is-required
        >
            {{ contractsCount }}
        </DFormRow>
        <DFormRow
            :label="__('contracts.pricing.preview_table.excluded_terminated_contracts')"
            is-required
        >
            {{ terminatedContractsCount }}
        </DFormRow>
        <DFormRow
            :label="__('contracts.pricings.price_communication')"
            is-required
        >
            {{ __(`contracts.pricings.communication.${props.priceCommunication}`) }}
        </DFormRow>
        <DFormRow
            :label="__('contracts.pricing.preview_table.inform_customers')"
            is-required
        >
            {{ informCustomers ? __('contracts.pricing.preview_table.yes') : __('contracts.pricing.preview_table.no') }}
        </DFormRow>
        <DFormRow
            :label="__('contracts.pricing.preview_table.single_email_per_customer')"
            is-required
        >
            {{ singleEmailPerCustomer ? __('contracts.pricing.preview_table.yes') : __('contracts.pricing.preview_table.no') }}
        </DFormRow>
        <DFormRow
            :label="__('contracts.pricings.mail_template')"
            is-required
        >
            {{ __(`contracts.pricings.mail_templates.${props.mailTemplate}`) }}
        </DFormRow>
        <DCard padding>
            <DRow
                align-horizontal="start"
                align-vertical="end"
                class="mb-3"
            >
                <DCol lg="2">
                    <DInput
                        id="customer"
                        v-model="filterData.customer"
                        :label="__('contracts.pricing.preview_table.customer')"
                        type="number"
                        pattern="^\d*$"
                        min="1"
                    />
                </DCol>
                <DCol lg="2">
                    <DInput
                        id="contract"
                        v-model="filterData.contract"
                        :label="__('contracts.pricing.preview_table.contract')"
                        type="number"
                        pattern="^\d*$"
                        min="1"
                    />
                </DCol>
                <DCol
                    lg="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage(props.newContracts)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </DCard>
        <DRow>
            <DCol>
                <DTable
                    :items="items"
                    :fields="fields"
                    hover
                >
                    <template #customer="{item}">
                        <td v-if="userPermissions.includes('customer.view')">
                            <Link :href="customerProfileRoute(item.customer.id)">
                                {{ item.customerFullName }}({{ item.customer.id }} {{ item.customer.company && `| ${item.customer.company}` }})
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.customerFullName }}({{ item.customer.company && `| ${item.customer.company}` }})
                        </td>
                    </template>
                    <template #contract="{item}">
                        <td v-if="userPermissions.includes('contract.view')">
                            <Link :href="contractRoute(item)">
                                {{ item.productName }} ({{ item.contract.id }})
                            </Link>
                        </td>
                        <td v-else>
                            {{ item.productName }} ({{ item.contract.id }})
                        </td>
                    </template>
                    <template #oldPrice="{item}">
                        <td>
                            <DRow>
                                <DCol>
                                    {{ priceFormatForDisplay(item.oldPrice.price, 2, item.customer?.market_region_id) }}/{{ item.oldPrice.billing_cycle }}M
                                </DCol>
                            </DRow>
                            <DRow>
                                <DCol>
                                    {{ __('contracts.pricing.preview_table.discount') }}: {{ item.oldPrice.discount_type === 'percentage' ? percentageFormatForDisplay(item.oldPrice.discount_value) ?? percentageFormatForDisplay(0) : item.oldPrice.discount_value ?? 0 }}
                                </DCol>
                            </DRow>
                        </td>
                    </template>
                    <template #newPrice="{item}">
                        <td>
                            <DRow>
                                <DCol v-if="item.newPrice.price === null">
                                    {{ __('contracts.pricing.not_applicable') }}
                                </DCol>
                                <DCol v-else>
                                    {{ priceFormatForDisplay(item.newPrice.price, 2, item.customer?.market_region_id) }}/{{ item.newPrice.accounting_period }}M
                                </DCol>
                            </DRow>
                            <DRow v-if="item.newPrice.price !== null">
                                <DCol>
                                    {{ __('contracts.pricing.preview_table.discount') }}: {{ item.newPrice.discount_type === 'percentage' ? percentageFormatForDisplay(item.newPrice.discount_value) ?? percentageFormatForDisplay(0) : item.newPrice.discount_value ?? 0 }}
                                </DCol>
                            </DRow>
                        </td>
                    </template>
                    <template #extraordinaryTermination="{item}">
                        <td>
                            {{ item.extraordinaryTermination ? 'yes' : 'no' }}
                        </td>
                    </template>
                    <template #actions="{item}">
                        <td>
                            <div style="display: flex; grid-gap: 10px">
                                <DButton
                                    icon="eye"
                                    mini
                                    @click="openModal(item)"
                                />
                            </div>
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="newContracts.last_page >= 1"
                    :active-page="newContracts.current_page"
                    :pages="newContracts.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
    </DCard>
    <EmailPreviewModal
        v-if="showModal"
        :show-modal="showModal"
        :display-data="displayData"
        :errors="errors"
        :mail-template="props.mailTemplate"
        @closeModal="closeModal"
    />
    <DFormControls
        :disabled="disabledActions"
        :cancel-route="cancelRoute"
        :submit-message="__('contracts.pricing.messages.apply')"
        submit-icon="check"
        @submit="submitForm"
    >
        <template #additional_button_before_submit>
            <DFab
                v-if="items.length > 0 && items[0].status === 'draft'"
                id="btn-apply-pricing"
                icon="download"
                color="secondary"
                :label="__('contracts.pricing.messages.domain_decoupling_csv')"
                @click="downloadDomainDecouplingCsv()"
            />
            <DFab
                id="btn-draft"
                :disabled="disabledActions"
                :icon="'content-save'"
                :label="__('contracts.pricing.messages.save_as_draft')"
                @click="submitForm('draft')"
            />
        </template>
    </DFormControls>
</template>

<script setup>
import { computed, ref } from 'vue'
import { route } from '~@/Composables/route'
import { router, usePage } from '@inertiajs/vue3'

import { __ } from '~@/Composables/translation'
import EmailPreviewModal from '~@/Components/Contracts/Pricing/EmailPreviewModal'

const props = defineProps({
    customersCount: {
        type: Number,
        default: null
    },
    contractsCount: {
        type: Number,
        default: null
    },
    terminatedContractsCount: {
        type: Number,
        default: null
    },
    priceCommunication: {
        type: String,
        default: 'gross'
    },
    informCustomers: {
        type: Boolean,
        default: false
    },
    singleEmailPerCustomer: {
        type: Boolean,
        default: true
    },
    pricingUploadId: {
        type: Number,
        required: true
    },
    items: {
        type: Array,
        default: () => {
            return []
        }
    },
    newContracts: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    mailTemplate: {
        type: String,
        default: 'default_pricing'
    }
})

const fields = [
    { key: 'customer', label: __('contracts.pricing.preview_table.customer') },
    { key: 'contract', label: __('contracts.pricing.preview_table.contract') },
    { key: 'oldPrice', label: __('contracts.pricing.preview_table.oldPrice') },
    { key: 'newPrice', label: __('contracts.pricing.preview_table.newPrice') },
    { key: 'changeDate', label: __('contracts.pricing.preview_table.changeDate') },
    { key: 'extraordinaryTermination', label: __('contracts.pricing.preview_table.extraordinary_termination') },
    { key: 'announcementDate', label: __('contracts.pricing.preview_table.announcement_date') },
    { key: 'actions', label: '' }
]

const filterData = ref(props.filters)
const showModal = ref(false)
let displayData = null
let errors = []

const closeModal = () => (showModal.value = false)
const openModal = async (value) => {
    errors = []
    displayData = null

    try {
        const response = await window.axios.post(route('contracts.pricing.preview-email'), {
            pricingUploadId: props.pricingUploadId,
            customer: value.customer.id,
            contractId: value.contract.id
        })
        displayData = response.data
    } catch (error) {
        const errorObject = error.response && error.response.data && error.response.data.errors
            ? error.response.data.errors
            : {}
        Object.keys(errorObject).forEach((field) => {
            errorObject[field].forEach((message) => {
                errors.push(message)
            })
        })
    }
    showModal.value = true
}

const cancelRoute = computed(() => {
    return route('contracts.pricing.cancel', { pricingUpload: props.pricingUploadId })
})

const userPermissions = computed(() => usePage().props.auth.permissions)

const disabledActions = computed(() => props.contractsCount < 1)

const customerProfileRoute = (id) => route('customers.customers.show', id)
const contractRoute = (value) => {
    return route('customers.customers.contracts.show', {
        customer: value.customer.id,
        contract: value.contract.id
    })
}
const submitForm = (status) => router.get(route('contracts.pricing.submit', { pricingUpload: props.pricingUploadId, status: status }))
const resetFilters = () => router.get(route('contracts.pricing.preview-upload', props.pricingUploadId))

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }
    router.get(route('contracts.pricing.preview-upload', props.pricingUploadId), query)
}

function downloadDomainDecouplingCsv () {
    const link = document.createElement('a')
    link.href = route('contracts.pricing.download-domain-decoupling-csv', props.pricingUploadId)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

</script>
